/**
 * @file Menu.js.
 * @author Lino Donegá
 * @copyright Cognatis GeoAnalytics & Big Data Ltda :: 2020
 */

import Request from '../components/http/request'
import Service from './Service'

const defaultJsonHeaders = {
  headers: {
    'Content-Type': 'application/json'
  }
}

/**
 * GeoLevel Management Service
 * @class
 */
class Menu extends Service {
  constructor (options, resource) {
    super(options)
    this.resource = resource
    this.request = new Request({tag: 'python'})
  }


  /**
   * Get Menu Environments
   *
   * @async
   * @function Get Menus
   * @param {Number} environmentId - environmentId
   * @return {Promise<json>} The json with the geoLevels
   */
  async getMenu (environmentId) {
    return new Promise((resolve, reject) => {
      if (typeof environmentId === 'undefined') reject(new Error('Esperado o identificador do ambiente'))
      console.log('### GET MENU ###')
      let endPoint = `/menu/${environmentId}`
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject())
    })
  }

  /**
   * Get Menu Environments
   *
   * @async
   * @function Get Menus
   * @param {Number} environmentId - environmentId
   * @return {Promise<json>} The json with the geoLevels
   */
  async getMenuSubmenubyDatavis (environmentId,layerType) {
    return new Promise((resolve, reject) => {
      if (typeof environmentId === 'undefined') reject(new Error('Esperado o identificador do ambiente'))
      if (typeof layerType === 'undefined') reject(new Error('Esperado o datavis do ambiente.'))
      let layer_datavis = layerType.map(type => `layer_datavis=${type}`).join('&');
      let endPoint = `/menu/submenu/${environmentId}?${layer_datavis}`
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject())
    })
  }
  /**
   * Get SubMenu Environments
   *
   * @async
   * @function Get Sub Menus
   * @param {Number} environmentId - environmentId
   * @param {Number} menuId - environmentId
   * @return {Promise<json>} The json with the geoLevels
   */
  async getSubMenu (environmentId,menuId) {
    return new Promise((resolve, reject) => {
      if (typeof environmentId === 'undefined') reject(new Error('Esperado o identificador do ambiente'))
      if (typeof menuId === 'undefined') reject(new Error('Esperado o identificador do Menu'))
      let endPoint = `/submenu/${environmentId}/${menuId}`
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject())
    })
  }
  /**
   * Get Layer
   *
   * @async
   * @function Get Layer
   * @param {Number} layerId - environmentId
   * @return {Promise<json>} The json with the geoLevels
   */
  async getLayer (layerId) {
    return new Promise((resolve, reject) => {
      if (typeof layerId === 'undefined') reject(new Error('Esperado o identificador do layerid'))
      let endPoint = `/layer/${layerId}`
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject())
    })
  }

  /**
   * Recuperar Categorias para uma Camada ou Submenu Específico
   *
   * Esta função assíncrona busca categorias de níveis geográficos associadas a uma camada ou submenu específico,
   * com base nos parâmetros fornecidos. Se nenhum ID de submenu for informado, a função assume a busca de dados
   * para um ambiente específico.
   *
   * @async
   * @function getCategories
   * @param {Number|null} submenu_id - ID do submenu, ou null se nenhum submenu estiver sendo alvo.
   * @param {Number} geolevel_targetId - ID do nível geográfico alvo para a requisição.
   * @param {Number} env_id - Identificador do ambiente, obrigatório para a requisição.
   * @param {Number} [alwaysontop=0] - Indicador opcional se a categoria deve sempre aparecer no topo (padrão é 0).
   * @param {Number} role_id - Identificador para a listagem de variáveis ou papéis.
   * @param {Number} include_options - Se deve incluir opções adicionais no resultado.
   * @param {Boolean} includeColumns - Se deve incluir as expressões no resultado.
   * @throws Lança um erro se o ID do ambiente ou o ID de listagem de variáveis não forem definidos.
   * @returns {Promise<Object>} Retorna uma Promise que resolve para um objeto JSON contendo os níveis geográficos (geoLevels).
   *
   * @example
   * // Exemplo de uso:
   * getCategories(10, 1, 2, 2, 1, 1)
   *   .then(data => console.log(data))
   *   .catch(err => console.error(err));
   */
  async getCategories(submenu_id, geolevel_targetId, env_id, alwaysontop = 0, role_id, include_options, includeColumns = true) {
    if (typeof env_id === 'undefined') throw new Error('Esperado o identificador do environment');
    if (typeof role_id === 'undefined') throw new Error('Esperado o id de listagem das variáveis');

    // Verifica se submenu_id e geolevel_targetId são indefinidos ou nulos
    let endPoint = (submenu_id == null && geolevel_targetId == null)
      ? `/expression/${env_id}/${alwaysontop}/${role_id}/${include_options}`
      : (geolevel_targetId == null)
      ? `/expression/${submenu_id}/${env_id}/${alwaysontop}/${role_id}/${include_options}`
      : `/expression/${submenu_id}/${geolevel_targetId}/${env_id}/${alwaysontop}/${role_id}/${include_options}`;

    endPoint = `${endPoint}?include_expressions=${includeColumns}`;

    try {
      let customHeader =  {
        headers: {
          'Content-Type': 'application/json',
          'Accept-Encoding':'gzip'
        }
      }


      const response = await this.request.get(endPoint, customHeader);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Recupera o submenu relacionado a um determinado layer pelo seu ID.
   *
   * Esta função faz uma requisição HTTP assíncrona para um endpoint que retorna os dados do submenu
   * de um layer específico, identificado pelo seu `layerId`. Caso o `layerId` não seja fornecido ou
   * seja indefinido, a função lança um erro.
   *
   * @async
   * @function getSubmenuByLayer
   * @param {string|number} layerId - O identificador único do layer para o qual se deseja obter o submenu.
   * @throws {Error} Lança um erro caso o `layerId` não seja fornecido ou seja indefinido, ou se ocorrer um erro na requisição.
   * @returns {Promise<Object>} Uma promessa que resolve para o objeto de resposta da requisição, que contém o submenu do layer.
   */
  async getSubmenuByLayer(layerId) {
    if (typeof layerId === 'undefined') throw new Error('Esperado o identificador do layer');

    const endPoint = `/submenu/layer/${layerId}`;

    try {
      const response = await this.request.get(endPoint, defaultJsonHeaders);
      return response;
    } catch (error) {
      throw error;
    }
  }


  /**
   * Recupera o submenu relacionado a um layer utilizando o seu GUID.
   *
   * Esta função faz uma requisição HTTP assíncrona para um endpoint que retorna os dados do submenu
   * de um layer específico, identificado pelo seu `layerGuid`. Caso o `layerGuid` não seja fornecido ou
   * seja indefinido, a função lança um erro.
   *
   * @async
   * @function getSubmenuByLayerGUID
   * @param {string} layerGuid - O GUID único do layer para o qual se deseja obter o submenu.
   * @throws {Error} Lança um erro caso o `layerGuid` não seja fornecido ou seja indefinido, ou se ocorrer um erro na requisição.
   * @returns {Promise<Object>} Uma promessa que resolve para o objeto de resposta da requisição, que contém o submenu do layer.
   */
  async getSubmenuByLayerGUID(layerGuid) {
    if (typeof layerGuid === 'undefined') throw new Error('Esperado o uid do layer');

    const endPoint = `/submenu/layer/${layerGuid}`;

    try {
      const response = await this.request.get(endPoint, defaultJsonHeaders);
      return response;
    } catch (error) {
      throw error;
    }
  }

}

export { Menu }
